import upperFirst from 'lodash/upperFirst'
import React, { ReactNode } from 'react'
import type { Engagement, FfpDashboardData, PeriodKey } from '../../types'
import { longMonth, PERIOD_NAMES, PERIODS, sliceSeries } from '../../utils'
import { PanelFreshness, PanelMeaning, PanelNoBenchmark, PanelNote, PanelSection, PanelTable } from '../AboutPanel'
import { PanelIncluded } from '../PanelIncluded'
import { EngagementPoint, shareText, workshopState, WorkshopState } from './utils'

export type PanelKey = 'engagement' | 'coaching' | 'workshops' | 'app'

export const PANEL_TITLES: Record<PanelKey, string> = {
  engagement: 'Engagement over time',
  coaching: 'Coaching',
  workshops: 'Workshops',
  app: 'The Wealthbit app',
}

type Props = { panel: PanelKey | null; data: FfpDashboardData; points: EngagementPoint[]; period: PeriodKey }

export const EngagementPanelContent = ({ panel, data, points, period }: Props) => {
  if (panel === 'engagement') return <EngagementPanel data={data} points={points} />
  if (panel === 'coaching') return <CoachingPanel data={data} period={period} />
  if (panel === 'workshops') return <WorkshopsPanel data={data} />
  if (panel === 'app') return <AppPanel data={data} points={points} />
  return null
}

const EveryoneEnrolled = ({ enrolled, detail }: { enrolled: number; detail: string }) => (
  <PanelSection heading="Who is included">
    <PanelIncluded included={`${enrolled} people`} of="enrolled" exclusions={[]} everyone={detail} />
  </PanelSection>
)

const SOURCES = {
  app: 'App: from app set-ups',
  coaching: 'Coaching: from coaching bookings',
  workshops: 'Workshops: from event sign-ins',
}

const TOTALS_NOTE = 'These are company totals, so they show however small they are. Only breakdowns that could point to someone, such as a workshop topic, are hidden.'

const Numbers = ({ columns, rows, note }: { columns: string[]; rows: string[][]; note?: string }) => (
  <PanelSection heading="The numbers behind the card">
    <PanelTable columns={columns} rows={rows} note={note} />
  </PanelSection>
)

const Sources = ({ children }: { children: ReactNode }) => (
  <PanelSection heading="Where the numbers come from">
    {children}
    <PanelNote>Our data updates every Monday.</PanelNote>
  </PanelSection>
)

// Coaching counts people who have since left, so it can pass enrolled.
const shareCell = (count: number, enrolled: number): string => (count === 0 ? 'None yet' : upperFirst(shareText(count, enrolled)))

const EngagementPanel = ({ data, points }: { data: FfpDashboardData; points: EngagementPoint[] }) => {
  const { enrolled, onboarded } = data.people
  const { captured_at, coaching_synced_at, workshops_imported_at } = data.freshness
  const rows = points.map((point) => [
    longMonth(point.month),
    shareCell(point.onboarded, enrolled),
    shareCell(point.app, enrolled),
    shareCell(point.coaching, enrolled),
    shareCell(point.workshops, enrolled),
  ])

  return (
    <>
      <PanelMeaning
        definition={`The share of the ${enrolled} enrolled people who have done each thing at least once since the programme started. Onboarded means they’ve finished their financial snapshot.`}
        working="These numbers only go up: once someone has been coached, they stay counted."
      />
      <EveryoneEnrolled
        enrolled={enrolled}
        detail={`Nobody is excluded. The share is out of all ${enrolled} enrolled people, including the ${enrolled - onboarded} who haven’t onboarded yet.`}
      />
      <Numbers columns={['Month', 'Onboarded', 'App', 'Coaching', 'Workshops']} rows={rows} note={TOTALS_NOTE} />
      <Sources>
        <PanelFreshness source="Enrolled: from your enrolment list" at={captured_at} />
        <PanelFreshness source={'Onboarded: from people’s financial snapshots'} at={captured_at} />
        <PanelFreshness source={SOURCES.app} at={captured_at} />
        <PanelFreshness source={SOURCES.coaching} at={coaching_synced_at} />
        <PanelFreshness source={SOURCES.workshops} at={workshops_imported_at} label="latest attendance recorded" />
      </Sources>
      <PanelNoBenchmark />
    </>
  )
}

const CoachingPanel = ({ data, period }: { data: FfpDashboardData; period: PeriodKey }) => {
  const { enrolled } = data.people
  const rows = sliceSeries(data.engagement.coaching.monthly, period).map((month) => [
    longMonth(month.month),
    String(month.contacted),
    String(month.booked),
    String(month.coached),
  ])

  return (
    <>
      <PanelMeaning
        definition={`The share of the ${enrolled} enrolled people who have had at least one coaching session. The steps below the bar count people at each stage, from enrolled to coached.`}
        working={'Counted once per person, from the month of their first session. Coaching records aren’t matched one-to-one with the enrolment list, so the steps show counts rather than percentages.'}
      />
      <EveryoneEnrolled enrolled={enrolled} detail="Nobody is excluded." />
      <Numbers columns={['Month', 'Contacted', 'Booked', 'Coached']} rows={rows} note={TOTALS_NOTE} />
      <Sources>
        <PanelFreshness source={SOURCES.coaching} at={data.freshness.coaching_synced_at} />
      </Sources>
      <PanelNoBenchmark />
    </>
  )
}

const WORKSHOP_WORKING: Record<WorkshopState, (minimum: number) => string> = {
  no_attendance: () => 'Attendance will show here after the first workshop. It usually comes through a few days after the event.',
  under_five: (minimum) => `Counted once per person. Every topic had fewer than ${minimum} people, so the topic numbers are hidden to protect anonymity.`,
  shown: () => 'Counted once per person. Attendance usually comes through a few days after a workshop. Watching a recording doesn’t count yet.',
}

// The server's own period counts: workshops held aren't sent month by month.
const workshopRows = (workshops: Engagement['workshops'], state: WorkshopState): string[][] => {
  if (state === 'no_attendance') return [['Since the start', 'None yet', '']]

  return PERIODS.map(({ key }) => {
    const { sessions_held, attendees } = workshops.periods[key]
    if (sessions_held === 0) return [PERIOD_NAMES[key], 'None', '']
    return [PERIOD_NAMES[key], String(sessions_held), String(attendees)]
  })
}

const WorkshopsPanel = ({ data }: { data: FfpDashboardData }) => {
  const { enrolled } = data.people
  const workshops = data.engagement.workshops
  const state = workshopState(workshops)
  const rows = workshopRows(workshops, state)

  return (
    <>
      <PanelMeaning
        definition={`Group sessions of about an hour on one topic, on site or online. This card shows the share of the ${enrolled} enrolled people who have attended at least one, and how many people came to each topic.`}
        working={WORKSHOP_WORKING[state](workshops.minimum_people)}
      />
      <EveryoneEnrolled enrolled={enrolled} detail="Nobody is excluded." />
      <Numbers columns={['Period', 'Workshops held', 'People attended']} rows={rows} note={TOTALS_NOTE} />
      <Sources>
        <PanelFreshness source={SOURCES.workshops} at={data.freshness.workshops_imported_at} label="latest attendance recorded" />
      </Sources>
      <PanelNoBenchmark />
    </>
  )
}

const AppPanel = ({ data, points }: { data: FfpDashboardData; points: EngagementPoint[] }) => {
  const { enrolled } = data.people
  const rows = points.map((point) => [longMonth(point.month), String(point.app)])

  return (
    <>
      <PanelMeaning
        definition={`The share of the ${enrolled} enrolled people who have set up the Wealthbit app.`}
        working={'Counted once per person, from the month they set it up. We don’t track logins yet, so this doesn’t show how often people use it.'}
      />
      <EveryoneEnrolled enrolled={enrolled} detail="Nobody is excluded." />
      <Numbers columns={['Month', 'Set up the app']} rows={rows} note={TOTALS_NOTE} />
      <Sources>
        <PanelFreshness source={SOURCES.app} at={data.freshness.captured_at} />
      </Sources>
      <PanelNoBenchmark />
    </>
  )
}
